import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Map as MapIcon, Scale, Shuffle, Sparkles } from 'lucide-react';

import { SiGePlanEditor } from './SiGePlanEditor';
import { KiAssistent } from './KiAssistent';
import { entwurfsRueckfrage } from './kiAssistentLogic';
import type { KiBruecke } from './kiAssistentLogic';
import { KoordinierungModul } from '../koordinierung/KoordinierungModul';
import { HandbuchModul } from '../handbuch/HandbuchModul';
import type { HandbuchNavigationsZiel } from '../handbuch/HandbuchModul';
import { ladePlanQuelle } from '../sige-einweisung/useEinweisungen';
import { calculateStats } from '../project-hub/calculateStats';
import { baueSigePlanKontext } from '../../services/aiAssistContext';
import { ladePlanungsphaseFuerKi } from '../../services/aiAssistPlanung';
import { idleExitHoldReasons } from '../../services/idleHold';
import { getNormalizedServices } from '../../services/projectLv';
import type { ProjectRowLike } from '../../services/projectLv';
import { supabase } from '../../services/supabaseClient';
import type { Report, ServiceItem } from '../../types';

/** Welcher der drei Reiter offen ist. Zugleich der Einstieg von aussen. */
export type SigePlanReiter = 'plan' | 'koordinierung' | 'handbuch';

interface Props {
    projectId: string;
    report: Report;
    userProfile?: any;
    /** Firmenstammdaten aus dem Profil (App.invoiceCompany). */
    company?: unknown;
    /** Briefkopf-Konfiguration (App.quoteConfigMemo). */
    quoteConfig?: unknown;
    /** Rechnungs-Stammdaten fuer die Fusszeile (App.invoiceConfigMemo). */
    invoiceConfig?: unknown;
    /**
     * Mit welchem Reiter geoeffnet wird. Aendert sich der Wert am stehenden
     * Modul (gemerkte Ansicht nach F5, spaeter der Assistent), springt der
     * Reiter mit — deshalb kein blosser Anfangswert.
     */
    startReiter?: SigePlanReiter;
    /**
     * Reiterwechsel nach oben melden. App.tsx haelt damit `view` nach; sonst
     * zeigten Seitenleiste und gemerkte Ansicht weiter auf den Reiter, mit dem
     * geoeffnet wurde.
     */
    onReiterChange?: (reiter: SigePlanReiter) => void;
    /** Zurueck in den Projekt-Hub. Haengt am Plan-Reiter. */
    onBack: () => void;
    onUpdate?: (report: Report) => void;
    /** Sprung in ein Modul AUSSERHALB dieser Huelle (Bericht, Vorankuendigung). */
    onNavigate?: (modul: HandbuchNavigationsZiel) => void;
    isReadOnly?: boolean;
}

/**
 * Huelle um die drei Werkzeuge am SiGe-Plan: Plan, Gewerke-Ueberschneidung,
 * Handbuch. Sie ersetzt drei Kacheln im Projekt-Hub durch eine.
 *
 * ═══ WARUM EINE HUELLE UND KEINE REITER IM PLAN-EDITOR ════════════════════
 * `SiGePlanEditor` sperrt bei `isReadOnly` ALLES oder nichts: er schreibt an
 * rund 25 Stellen unmittelbar nach Supabase und zeigt in der Nur-Ansicht
 * deshalb nur einen Hinweis statt seiner Oberflaeche (siehe den Kommentar an
 * seinem `isReadOnly`). Handbuch und Koordinierung sperren dagegen fein je
 * Aktion (`abgelehntWennNurAnsicht`) und bleiben lesend voll benutzbar.
 * Steckte man sie IN den Editor, erbten sie dessen Grobsperre — Nur-Ansicht-
 * Nutzer koennten beide Werkzeuge dann gar nicht mehr ansehen.
 *
 * Diese Huelle deutet `isReadOnly` deshalb NICHT in eine Rundum-Sperre um: sie
 * reicht die Flagge unveraendert an alle drei Module durch, jedes bleibt bei
 * seiner eigenen Auslegung. Die Reiterleiste selbst bleibt immer bedienbar —
 * auch waehrend der Plan-Reiter seinen Sperrhinweis zeigt.
 *
 * ═══ DIE MODULE SELBST BLEIBEN UNVERAENDERT ═══════════════════════════════
 * Alle drei werden nur eingebunden. Was sie an Kopfzeile, Werkzeugleiste und
 * eigenen Reitern mitbringen, bleibt stehen; die aeussere Leiste haelt sich
 * darum bewusst schlank und traegt keine Knoepfe.
 */
export const SigePlanModul: React.FC<Props> = ({
    projectId, report, userProfile, company, quoteConfig, invoiceConfig,
    startReiter = 'plan', onReiterChange, onBack, onUpdate, onNavigate,
    isReadOnly = false,
}) => {
    const [reiter, setReiter] = useState<SigePlanReiter>(startReiter);

    // Der Einstiegsreiter muss auch nach dem Mounten noch greifen: die
    // gemerkte Ansicht steht erst mit dem geladenen Projekt fest.
    useEffect(() => { setReiter(startReiter); }, [startReiter]);

    // ═══ KI-Assistent ═════════════════════════════════════════════════════
    // Das Fenster haengt an der Huelle und nicht in den drei Modulen: es ist
    // EIN Fenster, das weiss, wo man steht. Was es zeigen und uebernehmen darf,
    // sagt ihm der aktive Reiter ueber seine `KiBruecke` — die Daten liegen
    // dort, und dort steht auch der Schreibschutz.
    const [assistentOffen, setAssistentOffen] = useState(false);
    const [koordinierungBruecke, setKoordinierungBruecke] = useState<KiBruecke | null>(null);
    const [handbuchBruecke, setHandbuchBruecke] = useState<KiBruecke | null>(null);

    // Stabil, sonst meldete sich das Kindmodul bei jedem Zeichnen neu an.
    const meldeKoordinierung = useCallback((b: KiBruecke | null) => setKoordinierungBruecke(b), []);
    const meldeHandbuch = useCallback((b: KiBruecke | null) => setHandbuchBruecke(b), []);

    /**
     * Der Reiterwechsel darf keinen ungespeicherten Stand still wegwerfen.
     *
     * Nur der offene Reiter ist gemountet (siehe unten) — der Ordnungs-Entwurf
     * des Handbuchs und sein Herkunftsnachweis sind Komponentenzustand und
     * fallen mit dem Modul weg. Nach „Übernommen — die Punkte stehen jetzt im
     * ENTWURF" liegt der Reiter „Plan" einen Zentimeter neben der
     * Erfolgsmeldung; ohne Rueckfrage waere die Uebernahme mit einem Klick
     * wieder verloren.
     *
     * Kein `confirm()`: die Anwendung laeuft ggf. im Sandbox-iframe, der native
     * Dialoge blockt (dieselbe Ueberlegung wie in `SiGePlanEditor`). Deshalb
     * eine Inline-Rueckfrage direkt unter der Reiterleiste.
     */
    const [wechselFrage, setWechselFrage] = useState<{ ziel: SigePlanReiter; text: string } | null>(null);

    const vollziehe = (ziel: SigePlanReiter) => {
        setWechselFrage(null);
        setReiter(ziel);
        onReiterChange?.(ziel);
        // Der Kontext gehoert zum Reiter, mit dem das Fenster geoeffnet wurde.
        // Beim Wechsel schliesst es sich deshalb — sonst spraechen Antwort und
        // sichtbare Liste ueber verschiedene Dinge.
        setAssistentOffen(false);
    };

    const wechsle = (ziel: SigePlanReiter) => {
        // Woher der offene Entwurf gemeldet wird, steht bei
        // `HANDBUCH_ENTWURF_GRUND`: das Handbuch meldet ihn ohnehin am
        // Leerlauf-Rueckzug an, eine zweite Meldekette waere eine zweite
        // Wahrheit ueber denselben Sachverhalt.
        const frage = entwurfsRueckfrage(reiter, ziel, idleExitHoldReasons());
        if (frage) {
            setWechselFrage({ ziel, text: frage });
            return;
        }
        vollziehe(ziel);
    };

    // ═══ LV-Zaehler des Handbuchs ═════════════════════════════════════════
    // Er hing an der Handbuch-Kachel und faellt mit ihr weg. Er wandert an den
    // Handbuch-REITER und nicht an die SiGe-Plan-Kachel: die traegt bereits
    // ihren eigenen Zaehler ('sigeplan'), und zwei Kontingente auf einer
    // Kachel liessen sich nicht auseinanderhalten. Am Reiter steht er dort,
    // wo er gilt — und ist beim Oeffnen der Huelle sofort sichtbar, ohne dass
    // man das Handbuch aufschlagen muss.
    const globalData = report.metadata.globalData;
    const linkConfig = globalData?.linkedProjectConfig;
    const [mutterLeistungen, setMutterLeistungen] = useState<ServiceItem[] | null>(null);

    // Tochterprojekte buchen auf das LV der Mutter. Ohne dieses Nachladen zeigte
    // der Zaehler dort GAR NICHTS — genau der Verlust, der hier vermieden werden
    // soll. Die Abfrage laeuft nur bei Tochterprojekten; im Regelfall kostet sie
    // nichts.
    //
    // Geholt werden AUSSCHLIESSLICH die Leistungen, nicht das ganze
    // Mutterprojekt: `loadProjectById` zieht neun Kindtabellen und heilt dabei
    // Bilder im FREMDEN Projekt selbst — ein Schreibvorgang fuer einen
    // Zaehler an einer Reiterbeschriftung.
    useEffect(() => {
        let verworfen = false;
        const lade = async () => {
            if (!linkConfig || linkConfig.role !== 'child' || !linkConfig.linkedProjectId) {
                setMutterLeistungen(null);
                return;
            }
            const mutterId = linkConfig.linkedProjectId;
            try {
                const { data: zeilen, error } = await supabase
                    .from('project_services')
                    .select('*')
                    .eq('project_id', mutterId)
                    .order('lv_position', { ascending: true });
                if (error) throw error;
                if (verworfen) return;
                if (zeilen && zeilen.length > 0) {
                    setMutterLeistungen(getNormalizedServices({ project_services: zeilen }));
                    return;
                }
                // Rueckfall wie in `getNormalizedServices`: ein Projekt, das seit
                // der Umstellung auf `project_services` nie gespeichert wurde,
                // fuehrt sein LV noch im JSON. Nur dieser Altbestand kostet die
                // zweite Abfrage.
                const { data: zeile, error: zeilenFehler } = await supabase
                    .from('projects').select('data').eq('id', mutterId).single();
                // Ein Lesefehler darf NICHT als „die Mutter hat kein LV" gelten:
                // sonst zeigte der Zaehler eine leere Fremdliste statt des
                // eigenen Standes. Er faellt in den Fang und laesst den Wert leer.
                if (zeilenFehler) throw zeilenFehler;
                if (!verworfen) {
                    setMutterLeistungen(getNormalizedServices({ data: zeile?.data } as ProjectRowLike));
                }
            } catch (e) {
                // Der Zaehler ist Beiwerk; ein Fehler darf die Huelle nicht
                // aufhalten.
                console.error(e);
            }
        };
        void lade();
        return () => { verworfen = true; };
    }, [linkConfig]);

    const handbuchStats = useMemo(() => {
        if (!globalData) return null;
        // 'site_rules' ist die LV-Position „Baustellenordnung erstellen".
        // Beim Tochterprojekt zaehlt das LV der Mutter, sonst das eigene.
        return calculateStats(mutterLeistungen ?? (globalData.services || []), 'site_rules');
    }, [mutterLeistungen, globalData]);

    /**
     * Die Bruecke des PLAN-Reiters — sie liegt hier und nicht im Editor.
     *
     * Der Editor ist der Zustaendigkeitsbereich eines anderen Moduls und wird
     * fuer den Assistenten nicht angefasst. Er braucht auch nichts beizutragen:
     * die Planfassung wird beim Oeffnen des Fensters frisch gelesen
     * (`ladePlanQuelle` — dieselbe Auswahl der neuesten, nicht geloeschten
     * Fassung, die auch Handbuch und Koordinierung benutzen), und geschrieben
     * wird von hier aus ohnehin nichts.
     */
    const planBruecke = useMemo<KiBruecke>(() => ({
        oeffne: async () => {
            // Die Planungsphase geht als Baustein mit: ihre Merkmale (Bestand,
            // Erdarbeiten, Kran …) sagen dem Assistenten, welche Schritte im
            // Plan FEHLEN koennten. Fail-open — ohne sie `planung: null`.
            const [quelle, planung] = await Promise.all([
                ladePlanQuelle(projectId),
                ladePlanungsphaseFuerKi(projectId),
            ]);
            // Bereits erfasste Gefaehrdungen gehen je Schritt mit, damit der
            // Assistent sagen kann, was FEHLT, statt Vorhandenes zu wiederholen.
            const jeSchritt = new Map<string, string[]>();
            for (const g of quelle.gefaehrdungen || []) {
                const bisher = jeSchritt.get(g.arbeitsschritt_id) || [];
                bisher.push(g.bezeichnung);
                jeSchritt.set(g.arbeitsschritt_id, bisher);
            }
            const plan = quelle.sigeProject;
            return {
                paket: {
                    art: 'plan',
                    kontext: baueSigePlanKontext(
                        {
                            // Die BEZEICHNUNG der Baumassnahme, nicht ihre
                            // Anschrift — die fuehrt `sige_projekte` in einem
                            // eigenen Feld und sie geht bewusst nicht mit.
                            bauvorhaben: plan?.baumassnahme_name || report.metadata.projectName,
                            bauzeitVon: plan?.zeitraum_von || globalData?.startDate,
                            bauzeitBis: plan?.zeitraum_bis || globalData?.endDate,
                            hauptabschnitte: plan?.hauptabschnitte,
                        },
                        (quelle.arbeitsschritte || []).map(s => ({
                            ...s,
                            gefaehrdungen: jeSchritt.get(s.id) || [],
                        })),
                        planung,
                    ),
                },
                // Der Plan-Reiter uebernimmt NICHTS: dieses Modul ist gegenueber
                // dem Plan lesend, und der Editor gehoert einem anderen
                // Zustaendigkeitsbereich.
                darfUebernehmen: false,
                sperrgrund: 'Aus dem Plan-Reiter wird nichts übernommen. Der Assistent zeigt, '
                    + 'was ihm auffällt; eingetragen wird es im Plan-Editor — dort steht die '
                    + 'Fassung, auf die sich der Nachweis später beruft.',
            };
        },
        uebernimm: async () => {
            throw new Error('Aus dem Plan-Reiter wird nichts übernommen.');
        },
        // Bewusst NICHT `globalData`: das Objekt wechselt mit jedem neuen
        // `report` seine Identitaet, und das offene Fenster laese die Planquelle
        // dann ein zweites Mal. Nur die beiden wirklich benutzten Werte.
    }), [projectId, report.metadata.projectName, globalData?.startDate, globalData?.endDate]);

    const bruecke = reiter === 'plan' ? planBruecke
        : reiter === 'koordinierung' ? koordinierungBruecke
            : handbuchBruecke;

    // ═══ Zurueck ══════════════════════════════════════════════════════════
    // Aus Koordinierung und Handbuch fuehrt der Zurueck-Pfeil auf den
    // Plan-Reiter, nicht in den Hub: sonst spraenge man aus zwei Ebenen auf
    // einmal. Vom Plan-Reiter aus geht es dann wie gewohnt in den Hub.
    //
    // `HandbuchModul` benutzte `onBack` zusaetzlich fuer seine Hinweis-Knoepfe
    // „Stammdaten"/„Verteiler" (dort in `springe`). Die landeten damit auf dem
    // Plan-Reiter statt im Projekt-Hub, wo beide Erfassungen liegen. Das Modul
    // hat dafuer jetzt eine eigene Prop (`onHubSprung`); ohne sie verhaelt es
    // sich wie zuvor.
    const zurueckZumPlan = () => wechsle('plan');

    // Das Handbuch springt auch auf 'sigeplan' und 'koordinierung' — beides
    // liegt jetzt nebenan. Diese Ziele werden hier abgefangen und zu einem
    // Reiterwechsel; nur was ausserhalb liegt, geht nach oben.
    const handbuchNavigation = (ziel: HandbuchNavigationsZiel) => {
        if (ziel === 'sigeplan') { wechsle('plan'); return; }
        if (ziel === 'koordinierung') { wechsle('koordinierung'); return; }
        onNavigate?.(ziel);
    };

    const REITER: { key: SigePlanReiter; label: string; icon: React.ReactNode; zaehler?: { used: number, contracted: number } | null }[] = [
        { key: 'plan', label: 'Plan', icon: <MapIcon className="w-4 h-4" /> },
        { key: 'koordinierung', label: 'Gewerke-Überschneidung', icon: <Shuffle className="w-4 h-4" /> },
        { key: 'handbuch', label: 'Handbuch', icon: <Scale className="w-4 h-4" />, zaehler: handbuchStats },
    ];

    return (
        // `min-h-0` an beiden Ebenen: ein Flex-Kind schrumpft sonst nicht unter
        // seinen Inhalt, und das Scrollen der inneren Module greift nicht.
        <div data-glass-view="module-sige-plan" className="h-full flex flex-col bg-slate-50 min-h-0">

            {/* ─── Reiter ──────────────────────────────────────────────────
                Schlank gehalten (kein Titel, keine Knoepfe): der Plan-Editor
                bringt direkt darunter seine eigene Werkzeugleiste mit, und
                zwei volle Leisten uebereinander wirkten wie zwei
                Navigationsebenen. Machart wie in HandbuchModul und
                KoordinierungModul — dieselbe Leiste, eine Ebene hoeher. */}
            <nav
                aria-label="SiGe-Plan"
                data-glass="reiterleiste"
                className="bg-white border-b border-slate-200 px-2 sm:px-4 flex items-center gap-1 shrink-0 overflow-x-auto"
            >
                {REITER.map(r => {
                    const kontingent = r.zaehler && r.zaehler.contracted > 0 ? r.zaehler : null;
                    const erfuellt = !!kontingent && kontingent.used >= kontingent.contracted;
                    return (
                        <button
                            key={r.key}
                            type="button"
                            onClick={() => wechsle(r.key)}
                            aria-current={reiter === r.key ? 'page' : undefined}
                            className={`inline-flex items-center gap-2 px-3 py-2.5 text-[13px] font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${
                                reiter === r.key
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {r.icon} {r.label}
                            {kontingent && (
                                <span
                                    title={`Leistungsverzeichnis „Baustellenordnung erstellen": ${kontingent.used} von ${kontingent.contracted} beauftragt`}
                                    className={`text-[10px] font-black rounded-full px-1.5 py-0.5 border ${
                                        erfuellt
                                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                            : 'bg-slate-100 text-slate-500 border-slate-200'
                                    }`}
                                >
                                    {kontingent.used} / {kontingent.contracted}
                                </span>
                            )}
                        </button>
                    );
                })}

                {/* Ein Knopf fuer alle drei Reiter. Er faellt nicht weg, wenn
                    das Projekt nur gelesen werden darf: FRAGEN ist immer
                    erlaubt, uebernehmen entscheidet der Reiter. */}
                <button
                    type="button"
                    onClick={() => setAssistentOffen(true)}
                    title="KI-Assistent zum aktiven Reiter — schlägt vor, übernimmt nichts von allein"
                    className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 my-1 text-[12px] font-bold text-violet-700 bg-violet-50 border border-violet-100 rounded-lg hover:bg-violet-100 shrink-0"
                >
                    <Sparkles className="w-3.5 h-3.5" /> Assistent
                </button>
            </nav>

            {/* Rueckfrage vor dem Verwerfen — sie steht dort, wo geklickt
                wurde, und nennt beide Auswege beim Namen. */}
            {wechselFrage && (
                <div className="bg-amber-50 border-b border-amber-200 px-3 sm:px-4 py-3 flex flex-wrap items-start gap-3 shrink-0">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[13px] text-amber-900 flex-1 min-w-[14rem]">{wechselFrage.text}</p>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={() => setWechselFrage(null)}
                            className="px-3 py-1.5 text-[12px] font-bold text-amber-900 bg-white border border-amber-200 rounded-lg hover:bg-amber-100"
                        >
                            Im Handbuch bleiben
                        </button>
                        <button
                            type="button"
                            onClick={() => vollziehe(wechselFrage.ziel)}
                            className="px-3 py-1.5 text-[12px] font-bold text-white bg-amber-600 rounded-lg hover:bg-amber-700"
                        >
                            Wechseln und verwerfen
                        </button>
                    </div>
                </div>
            )}

            {assistentOffen && (
                <KiAssistent
                    reiter={reiter}
                    bruecke={bruecke}
                    onSchliessen={() => setAssistentOffen(false)}
                    onSprung={wechsle}
                />
            )}

            {/* Nur der offene Reiter wird gemountet: jedes der drei Module
                laedt beim Mounten seine eigene Lage aus der Datenbank — alle
                drei stehen zu lassen hiesse, drei Abfragen fuer eine sichtbare
                Ansicht zu fahren. */}
            <div className="flex-1 min-h-0">
                {reiter === 'plan' && (
                    <SiGePlanEditor
                        project={{ id: projectId, data: report, name: report.metadata.projectName }}
                        userProfile={userProfile}
                        onBack={onBack}
                        onUpdate={onUpdate}
                        isReadOnly={isReadOnly}
                    />
                )}
                {reiter === 'koordinierung' && (
                    <KoordinierungModul
                        projectId={projectId}
                        projektName={report.metadata.projectName}
                        projektNummer={report.metadata.projectNumber}
                        projektAdresse={report.metadata.projectAddress}
                        userProfile={userProfile}
                        company={company}
                        quoteConfig={quoteConfig}
                        invoiceConfig={invoiceConfig}
                        onBack={zurueckZumPlan}
                        onKiBruecke={meldeKoordinierung}
                        isReadOnly={isReadOnly}
                    />
                )}
                {reiter === 'handbuch' && (
                    <HandbuchModul
                        projectId={projectId}
                        metadata={report.metadata}
                        globalData={globalData}
                        vorankuendigung={report.vorankuendigung}
                        // Bericht und Schreibweg fuer den Abschlussschritt nach der
                        // Freigabe (Plan Paketposition B5) — die Huelle hat beides.
                        report={report}
                        onUpdate={onUpdate}
                        userProfile={userProfile}
                        company={company}
                        quoteConfig={quoteConfig}
                        invoiceConfig={invoiceConfig}
                        onBack={zurueckZumPlan}
                        onHubSprung={onBack}
                        onNavigate={handbuchNavigation}
                        onKiBruecke={meldeHandbuch}
                        isReadOnly={isReadOnly}
                    />
                )}
            </div>
        </div>
    );
};
